import { useState, useEffect, useMemo } from 'react';
import { Document, DocumentStatus, WorkflowTemplateInstance, ApprovalStep, Approver } from '../../types/document.ts';
import { ArrowLeft, Save, X, Plus, Trash2, UserCircle, ChevronDown, ChevronUp, Clock, WorkflowIcon, RotateCcw, FolderOpen } from 'lucide-react';
import SharePointBrowser from '../../components/SharePointBrowser.tsx';
import { getOfficeHref } from '../../utils/sharepointPicker.ts';
import { useNotify, useRedirect } from 'react-admin';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams, useLocation } from 'react-router-dom';
import { fetchApprovers } from '../../services/approverService.ts';
import { updateDocument, overwriteDocumentFile, getDocumentById, deleteDocument, getDocumentApprovalInfo, replaceApproverWithDeputy, getDocumentWorkflowInstance, checkTitleUnique, checkDocNumUnique, recallDocumentToDraft } from '../../services/documentService.ts';

export function DocumentManageEdit() {
  const { id } = useParams(); // Get document ID from URL
  const documentId = id || null;
  const location = useLocation();
  const isRevision = new URLSearchParams(location.search).get('revision') === 'true';
  const isOverwrite = new URLSearchParams(location.search).get('mode') === 'overwrite';
  const notify = useNotify();
  const redirect = useRedirect();
  const handleBack = () => {
    redirect('list', 'documentsList');
  };

  const normalizeSharepointUrl = (rawUrl: string) => {
    const trimmedUrl = rawUrl.trim();
    if (!trimmedUrl) return '';

    return /^https?:\/\//i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;
  };
  

  // Fetch approvers from userService API
  const [approversPool, setApproversPool] = useState<Approver[]>([]);
  const [existingDocData, setExistingDocData] = useState<Document | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(!!documentId);
  const [saving, setSaving] = useState(false);
  const [userNameMap, setUserNameMap] = useState<Record<string, string>>({});
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [showDeputyModal, setShowDeputyModal] = useState(false);
  const [selectedStepInstanceId, setSelectedStepInstanceId] = useState<string>('');
  const [selectedFromUserId, setSelectedFromUserId] = useState<string>('');
  const [selectedDeputyUserId, setSelectedDeputyUserId] = useState<string>('');
  const [deputyComment, setDeputyComment] = useState('');
  const [savingDeputy, setSavingDeputy] = useState(false);
  const [selectedTemplateInstanceId, setSelectedTemplateInstanceId] = useState<string>('');
  const [selectedDepartmentTitle, setSelectedDepartmentTitle] = useState<string>('all');
  const [approverKeyword, setApproverKeyword] = useState('');
  const [template, setTemplate] = useState<WorkflowTemplateInstance>({
    steps: [],
  });

  useEffect(() => {
    const loadApprovers = async () => {
      try {
        console.log('Loading approvers...');
        const data = await fetchApprovers();
        console.log('Approvers loaded:', data.length, 'items');
        setAllUsers(data); // Set allUsers for deputy functionality
        const map: Record<string, string> = {};
        data.forEach((user: any) => {
          const userId = user?.id;
          const userName = user?.name;
          if (userId != null && userName) {
            map[String(userId)] = String(userName);
          }
        });
        setUserNameMap(map); // Set userNameMap for display names
        setApproversPool(data.map((approver: any) => ({
          ...approver,
          departmentTitleId: String(approver.departmentTitleId || ''),
          userId: String(approver.id || ''),
          selectorType: 'user',
          selectorValue: String(approver.id || '')
        })));
        console.log('Approvers pool set with', data.length, 'items');
      } catch (error) {
        console.error('Failed to load users:', error);
        // Fallback to empty array if API fails
        setApproversPool([]);
        setAllUsers([]);
        setUserNameMap({});
      }
    };

    loadApprovers();
  }, []);

  // Load document from backend if documentId exists
  useEffect(() => {
    if (documentId) {
      const loadDocument = async () => {
        try {
          const [doc, approvalRecords] = await Promise.all([
            getDocumentById(documentId),
            getDocumentApprovalInfo(documentId),
          ]);

          const enrichedDoc: Document = {
            ...doc,
            approvalHistory: approvalRecords.length > 0 ? approvalRecords : (doc.approvalHistory || []),
          };

          setExistingDocData(enrichedDoc as any);

          // Only extract approval steps from approval records if workflow instance doesn't exist
          // This serves as a fallback for documents without workflow instances
          if (approvalRecords && approvalRecords.length > 0) {
            // Group approval records by step to reconstruct steps
            const stepMap = new Map<number, ApprovalStep>();

            approvalRecords.forEach((record) => {
              const stepNo = record.level || 1;
              if (!stepMap.has(stepNo)) {
                stepMap.set(stepNo, {
                  id: `step-${stepNo}`,
                  stepNo: stepNo,
                  Name: `Step ${stepNo}`,
                  stepType: 'single',
                  approvers: [],
                  approvalRule: 'all',
                  timeLimit: 1,
                  description: '',
                  isOptional: false,
                });
              }
            });

            // Convert map to array and sort by step number
            const steps = Array.from(stepMap.values()).sort((a, b) => a.stepNo - b.stepNo);
            // Only set if no workflow instance steps exist
            setTemplate(prev => prev.steps.length === 0 ? { ...prev, steps } : prev);
          }

          setLoadingDoc(false);
        } catch (error: any) {
          console.error('Failed to load document:', error);
          notify(`Failed to load document: ${error.message}`, { type: 'error' });
          setLoadingDoc(false);
        }
      };
      loadDocument();
    } else {
      setLoadingDoc(false);
    }
  }, [documentId, notify]);

  // Load workflow instance data if documentId exists
  useEffect(() => {
    if (documentId && approversPool.length > 0) {
      const loadWorkflowInstance = async () => {
        try {
          console.log('Loading workflow instance for document:', documentId);
          const workflowInstance = await getDocumentWorkflowInstance(documentId);
          console.log('Workflow instance loaded:', workflowInstance);
          if (workflowInstance) {
            const workflowSteps = Array.isArray(workflowInstance)
              ? workflowInstance
              : (workflowInstance.steps || []);
            const templateInstanceId = Array.isArray(workflowInstance)
              ? (workflowInstance[0]?.instanceId || workflowInstance[0]?.templateInstanceId || workflowInstance[0]?.templateId || '')
              : (workflowInstance.templateInstanceId || workflowInstance.instanceId || workflowInstance.templateId || '');
            setSelectedTemplateInstanceId(String(templateInstanceId || ''));

            if (workflowSteps.length > 0) {
              console.log('Processing steps:', workflowSteps);
              const processedSteps = workflowSteps.map((step: any) => {
                console.log('Processing step:', step);
                const processedApprovers = [
                  // Process individual approvers
                  ...(step.approvers || []).map((approverData: any) => {
                    console.log('Processing approver:', approverData);
                    // Look up full user info from approversPool
                    const foundApprover = approversPool.find(a => a.id === approverData.userId?.toString());
                    console.log('Found approver in pool:', foundApprover);
                    if (foundApprover) {
                      return {
                        ...foundApprover,
                        selectorType: 'user',
                        selectorValue: approverData.userId?.toString() || '',
                      };
                    }
                    return {
                      id: approverData.userId?.toString() || approverData.id?.toString() || '',
                      name: approverData.userName || approverData.name || '',
                      role: 'User',
                      email: '',
                      selectorType: 'user',
                      selectorValue: approverData.userId?.toString() || '',
                      userId: approverData.userId?.toString() || '',
                    };
                  }),
                  // Process department titles
                  ...(step.departmentTitles || []).map((deptData: any) => {
                    console.log('Processing department title:', deptData);
                    return {
                      id: `dept-${deptData.departmentTitleId}`,
                      name: deptData.departmentTitle || '',
                      role: 'Department',
                      email: '',
                      departmentTitle: deptData.departmentTitle || '',
                      departmentTitleId: deptData.departmentTitleId?.toString() || '',
                      selectorType: 'departmentTitle',
                      selectorValue: deptData.departmentTitleId?.toString() || '',
                    };
                  })
                ];
                console.log('Processed approvers for step:', processedApprovers);
                return {
                  id: step.id.toString(),
                  stepNo: step.stepNo || step.stepNumber || 0,
                  Name: step.name || step.stepName || '',
                  stepType: step.stepType || 'single',
                  approvers: processedApprovers,
                  approvalRule: step.approvalRule || 'all',
                  timeLimit: step.timeLimit,
                  description: step.description || '',
                  isOptional: step.isOptional || false,
                };
              });
              console.log('Setting template steps:', processedSteps);
              setTemplate(prevTemplate => ({
                ...prevTemplate,
                steps: processedSteps
              }));
            }
          } else {
            console.log('No workflow instance found');
          }
        } catch (error) {
          console.error('Failed to load workflow instance:', error);
          // If workflow instance loading fails, keep existing logic from approval records
        }
      };
      loadWorkflowInstance();
    } else {
      console.log('Skipping workflow instance load - documentId:', documentId, 'approversPool length:', approversPool.length);
    }
  }, [documentId, approversPool]);

  const existingDoc = existingDocData;
  
  const [formData, setFormData] = useState({
    title: existingDoc?.title || '',
    description: existingDoc?.description || '',
    category: existingDoc?.category || 'SOP',
    ownerId: (existingDoc as any)?.ownerId ? String((existingDoc as any).ownerId) : ((existingDoc as any)?.owner ? String((existingDoc as any).owner) : ''),
    currentVersion: existingDoc?.currentVersion || '',
    createdAt: existingDoc?.createdAt ? String(existingDoc.createdAt) : '',
    versionChanges: '',
  });

  // Update form data when existingDoc is loaded
  useEffect(() => {
    if (existingDoc) {
      setFormData({
        title: existingDoc.title || '',
        description: existingDoc.description || '',
        category: existingDoc.category || 'SOP',
        ownerId: (existingDoc as any).ownerId ? String((existingDoc as any).ownerId) : ((existingDoc as any).owner ? String((existingDoc as any).owner) : ''),
        currentVersion: existingDoc.currentVersion || '',
        createdAt: existingDoc.createdAt ? String(existingDoc.createdAt) : '',
        versionChanges: existingDoc.versionChanges || '',
      });
      setStatus(existingDoc.status || 'Draft');
      setDocNum(existingDoc.docNum || existingDoc.documentNumber || '1.0');
      // Store existing file URL for editing
      const fileUrl = existingDoc.draftFileUrl || null;
      setExistingFileUrl(fileUrl);
      setSharepointUrl(fileUrl || '');
    }
  }, [existingDoc]);

  // const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [sharepointUrl, setSharepointUrl] = useState<string>('');
  const [showSpBrowser, setShowSpBrowser] = useState(false);
  const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<DocumentStatus>('Draft');
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [showApprovalSection, setShowApprovalSection] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [recalling, setRecalling] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [docNum, setDocNum] = useState('1.0');
  const [validatingDocNum, setValidatingDocNum] = useState(false);
  const [titleValidation, setTitleValidation] = useState<{ status: 'idle' | 'checking' | 'valid' | 'invalid'; message?: string }>({ status: 'idle' });
  const [docNumValidation, setDocNumValidation] = useState<{ status: 'idle' | 'checking' | 'valid' | 'invalid'; message?: string }>({ status: 'idle' });

  useEffect(() => {
    if (!documentId || isRevision) {
      setTitleValidation({ status: 'idle' });
      return;
    }

    const normalizedTitle = formData.title.trim();
    if (!normalizedTitle) {
      setTitleValidation({ status: 'idle' });
      return;
    }

    setTitleValidation({ status: 'checking' });
    const timer = setTimeout(async () => {
      try {
        const result = await checkTitleUnique(normalizedTitle, documentId);
        if (result.isValid) {
          setTitleValidation({ status: 'valid', message: 'Title is available' });
        } else {
          setTitleValidation({ status: 'invalid', message: result.message || 'Document title already exists' });
        }
      } catch (error: any) {
        setTitleValidation({ status: 'invalid', message: error?.message || 'Failed to validate title' });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.title, documentId, isRevision]);

  useEffect(() => {
    if (!documentId || isRevision) {
      setDocNumValidation({ status: 'idle' });
      return;
    }

    const normalizedDocNum = docNum.trim();
    if (!normalizedDocNum) {
      setDocNumValidation({ status: 'idle' });
      return;
    }

    setDocNumValidation({ status: 'checking' });
    const timer = setTimeout(async () => {
      try {
        const result = await checkDocNumUnique(normalizedDocNum, documentId);
        if (result.isValid) {
          setDocNumValidation({ status: 'valid', message: 'DocNum is valid' });
        } else {
          setDocNumValidation({ status: 'invalid', message: result.message || 'DocNum validation failed' });
        }
      } catch (error: any) {
        setDocNumValidation({ status: 'invalid', message: error?.message || 'Failed to validate DocNum' });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [docNum, documentId, isRevision]);

  const departmentTitleOptions = useMemo(() => {
    const map = new Map<string, { title: string; id: string }>();
    approversPool.forEach(approver => {
      const title = String(approver.departmentTitle || '').trim();
      if (!title) return;
      const id = String(approver.departmentTitleId || '').trim();
      if (!map.has(title)) {
        map.set(title, { title, id });
      } else if (!map.get(title)?.id && id) {
        map.set(title, { title, id });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [approversPool]);

  const selectedDepartmentTitleId = useMemo(() => {
    if (selectedDepartmentTitle === 'all') return '';
    return departmentTitleOptions.find(option => option.title === selectedDepartmentTitle)?.id || '';
  }, [departmentTitleOptions, selectedDepartmentTitle]);

  const currentStep = useMemo(() => {
    if (!currentStepId) return undefined;
    return template.steps.find((s: ApprovalStep) => s.id === currentStepId);
  }, [currentStepId, template.steps]);

  const filteredApprovers = useMemo(() => {
    const keyword = approverKeyword.trim().toLowerCase();
    return approversPool.filter(approver => {
      const matchesDepartment =
        selectedDepartmentTitle === 'all' ||
        (approver.departmentTitle || '') === selectedDepartmentTitle;

      if (!matchesDepartment) return false;
      if (!keyword) return true;

      const target = `${approver.name} ${approver.email} ${approver.departmentTitle || ''}`.toLowerCase();
      return target.includes(keyword);
    });
  }, [approverKeyword, approversPool, selectedDepartmentTitle]);

  const buildDepartmentSelectorId = (title: string, departmentTitleId?: string) => {
    const normalizedId = String(departmentTitleId || '').trim();
    return normalizedId ? `deptid:${normalizedId}` : `dept:${title.trim()}`;
  };

  const createDepartmentTitleApprover = (title: string, departmentTitleId?: string): Approver => {
    const normalized = title.trim();
    const normalizedId = String(departmentTitleId || '').trim();
    return {
      id: buildDepartmentSelectorId(normalized, normalizedId),
      userId: '',
      name: normalized,
      role: 'DepartmentTitle',
      email: '',
      departmentTitle: normalized,
      departmentTitleId: normalizedId,
      selectorType: 'departmentTitle',
      selectorValue: normalizedId || normalized
    };
  };

  const closeApproverModal = () => {
    setShowApproverModal(false);
    setCurrentStepId(null);
    setSelectedDepartmentTitle('all');
    setApproverKeyword('');
  };

  const categories = ['SOP', 'QMS', 'Forms', 'Policy', 'SDS'];
  const selectedOwner = approversPool.find(user => user.id === formData.ownerId) || null;

  const toDatePickerValue = (value?: string | null) => {
    if (!value) return null;
    const normalized = String(value).trim();
    const parsed = /^\d{4}-\d{2}-\d{2}$/.test(normalized)
      ? new Date(`${normalized}T00:00:00`)
      : new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const toDateOnlyString = (value: Date | null) => {
    if (!value || Number.isNaN(value.getTime())) return '';
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toCreatedAtDateString = (value?: string | null) => {
    const normalized = String(value || '').trim();
    if (!normalized) return existingDoc?.createdAt;

    const parsed = /^\d{4}-\d{2}-\d{2}$/.test(normalized)
      ? new Date(`${normalized}T00:00:00`)
      : new Date(normalized);

    if (Number.isNaN(parsed.getTime())) {
      return existingDoc?.createdAt;
    }

    return toDateOnlyString(parsed);
  };

  // const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     setUploadedFile(file);
  //   }
  // };

  // const handleRemoveFile = () => {
  //   // In edit mode, revert to showing existing file
  //   if (documentId && existingFileUrl) {
  //     setUploadedFile(null);
  //   } else {
  //     setUploadedFile(null);
  //   }
  // };

  // Add approver to step
  const handleAddApprover = (stepId: string, approver: Approver) => {
    setTemplate(prev => ({
      ...prev,
      steps: prev.steps.map(s =>
        s.id === stepId ? { ...s, approvers: [...s.approvers, approver] } : s
      )
    }));
  };

  // Remove approver from step
  const handleRemoveApprover = (stepId: string, approverId: string) => {
    setTemplate(prev => ({
      ...prev,
      steps: prev.steps.map(s =>
        s.id === stepId ? { ...s, approvers: s.approvers.filter(a => a.id !== approverId) } : s
      )
    }));
  };

  // Remove step
  const handleRemoveStep = (stepId: string) => {
    if (true) {
      setTemplate(prevTemplate => ({
        ...prevTemplate,
        steps: prevTemplate.steps
          .filter(s => s.id !== stepId)
          .map((s, idx) => ({ ...s, stepNo: idx + 1 }))
      }));
    }
  };

  // Update step field
  const updateStepField = (stepId: string, field: string, value: any) => {
    setTemplate(prevTemplate => ({
      ...prevTemplate,
      steps: prevTemplate.steps.map(step =>
        step.id === stepId ? { ...step, [field]: value } : step
      )
    }));
  };

  // Add step
  const handleAddStep = () => {
    const newStep: ApprovalStep = {
      id: `custom-${Date.now()}`,
      stepNo: template.steps.length + 1,
      Name: `Custom Step ${template.steps.length + 1}`,
      stepType: 'single',
      approvers: [],
      approvalRule: 'all',
      timeLimit: 2,
      description: '',
      isOptional: false
    };
    setTemplate(prevTemplate => ({
      ...prevTemplate,
      steps: [...prevTemplate.steps, newStep]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      notify('Document title is required', { type: 'warning' });
      return;
    }

    if (!docNum.trim()) {
      notify('DocNum is required', { type: 'warning' });
      return;
    }

    if (!isRevision) {
      if (titleValidation.status === 'checking' || docNumValidation.status === 'checking') {
        notify('Validating title/doc number, please wait...', { type: 'info' });
        return;
      }

      if (titleValidation.status === 'invalid') {
        notify(titleValidation.message || 'Document title validation failed', { type: 'error' });
        return;
      }

      if (docNumValidation.status === 'invalid') {
        notify(docNumValidation.message || 'DocNum validation failed', { type: 'error' });
        return;
      }
    }

    if (!formData.ownerId) {
      notify('Please select an owner', { type: 'warning' });
      return;
    }
    
    // Validate approval workflow if submitting for review
    if (status === 'Review' && template.steps.length === 0) {
      notify('Please configure the approval workflow before submitting for review', { type: 'warning' });
      return;
    }

    if (status === 'Review') {
      const stepsWithoutApprovers = template.steps.filter((s: ApprovalStep) => s.approvers.length === 0 && !s.isOptional);
      if (stepsWithoutApprovers.length > 0) {
        const stepNames = stepsWithoutApprovers.map((s: ApprovalStep) => s.Name).join(', ');
        notify(`The following required steps do not have approvers assigned: ${stepNames}`, { type: 'warning' });
        return;
      }
    }

    if (!sharepointUrl.trim()) {
      notify('Document file URL is required. Please enter a SharePoint URL or select a file by clicking Browse.', { type: 'warning' });
      return;
    }
    const SHAREPOINT_BASE = 'https://bodcolimited.sharepoint.com/sites/QMS';
    if (!normalizeSharepointUrl(sharepointUrl).startsWith(SHAREPOINT_BASE)) {
      notify('Invalid file URL. The URL must start with https://bodcolimited.sharepoint.com/sites/QMS. Please re-enter the correct SharePoint file path.', { type: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (!isRevision && documentId) {
        setValidatingDocNum(true);
        const [titleCheckResult, docNumCheckResult] = await Promise.all([
          checkTitleUnique(formData.title, documentId),
          checkDocNumUnique(docNum.trim(), documentId),
        ]);

        if (!titleCheckResult.isValid) {
          setTitleValidation({ status: 'invalid', message: titleCheckResult.message || 'Document title already exists' });
          notify(titleCheckResult.message || 'Document title validation failed', { type: 'error' });
          setSaving(false);
          return;
        }

        if (!docNumCheckResult.isValid) {
          setDocNumValidation({ status: 'invalid', message: docNumCheckResult.message || 'DocNum validation failed' });
          notify(docNumCheckResult.message || 'DocNum validation failed', { type: 'error' });
          setSaving(false);
          return;
        }

        setTitleValidation({ status: 'valid', message: 'Title is available' });
        setDocNumValidation({ status: 'valid', message: 'DocNum is valid' });
      }

      const currentUserId = parseInt(localStorage.getItem('userId') || '1000');
      const currentTime = new Date().toISOString();
      
      const documentData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        ownerId: /^\d+$/.test(formData.ownerId) ? Number(formData.ownerId) : formData.ownerId,
        currentVersion: formData.currentVersion || existingDoc?.currentVersion || '1.0',
        createdAt: toCreatedAtDateString(formData.createdAt),
        createdDate: toCreatedAtDateString(formData.createdAt),
        docNum,
        tags: Array.isArray(existingDoc?.tags) ? existingDoc.tags : [],
        draftFileUrl: normalizeSharepointUrl(sharepointUrl) || existingFileUrl || undefined,
        // file: uploadedFile || undefined,
        status: status,
        approvalSteps: template.steps.length > 0 ? template.steps : undefined,
        documentNumber: docNum,
        createdBy: existingDoc?.createdBy ? parseInt(String(existingDoc.createdBy)) : currentUserId,
        updatedBy: currentUserId,
        updatedAt: currentTime,
        versionChanges: formData.versionChanges || undefined,
        templateInstanceId: selectedTemplateInstanceId || undefined,
      };

      if (!documentId) {
        notify('Cannot save: Document ID is missing', { type: 'error' });
        setSaving(false);
        return;
      }

      if (isOverwrite) {
        const url = normalizeSharepointUrl(sharepointUrl) || existingFileUrl;
        if (!url) {
          notify('Please enter a document URL', { type: 'warning' });
          setSaving(false);
          return;
        }
        await overwriteDocumentFile(documentId, { ...documentData, draftFileUrl: url });
        notify('Document overwritten successfully', { type: 'success' });
      } else if (isRevision) {
        // For revision, require a SharePoint URL
        if (!sharepointUrl) {
          notify('Please enter a SharePoint URL for the revision', { type: 'warning' });
          setSaving(false);
          return;
        }
        const result = await updateDocument(documentId, {
          ...documentData,
          draftFileUrl: normalizeSharepointUrl(sharepointUrl),
        });
        notify(`Document revision started successfully! Current Version: ${result.currentVersion}`, { type: 'success' });
      } else {
        const result = await updateDocument(documentId, documentData);
        notify(`Document updated successfully! Number: ${result.documentNumber}`, { type: 'success' });
      }
      
      if (status === 'Review' && template.steps.length > 0) {
        notify(`Approval workflow configured with ${template.steps.length} steps`, { type: 'info' });
      }
      
      // Redirect to list page after successful save
      setTimeout(() => {
        redirect('list', 'documentsList');
      }, 1500);
    } catch (error: any) {
      console.error('Failed to save document:', error);
      notify(`Failed to save document: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setValidatingDocNum(false);
      setSaving(false);
    }
  };

  const handleSaveAsDraft = () => {
    setStatus('Draft');
    setTimeout(() => {
      const submitBtn = document.getElementById('submit-btn') as HTMLButtonElement;
      submitBtn?.click();
    }, 0);
  };

  // const handleSubmitForReview = () => {
  //   setStatus('Review');
  //   setTimeout(() => {
  //     const submitBtn = document.getElementById('submit-btn') as HTMLButtonElement;
  //     submitBtn?.click();
  //   }, 0);
  // };

  const handleDelete = async () => {
    if (!documentId) return;
    
    setDeleting(true);
    try {
      await deleteDocument(documentId);
      notify('Document deleted successfully!', { type: 'success' });
      setShowDeleteModal(false);
      // Redirect to list page after deletion
      setTimeout(() => {
        redirect('list', 'documentsList');
      }, 1000);
    } catch (error: any) {
      console.error('Failed to delete document:', error);
      notify(`Failed to delete document: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const isInReviewStatus = String(existingDoc?.status ?? status).trim().toLowerCase() === 'review';

  const handleRecallToDraft = async () => {
    if (!documentId) return;

    if (!isInReviewStatus) {
      notify('Recall is only available when document is In Review', { type: 'warning' });
      return;
    }

    notify('Recalling document and reinitializing approval flow back to Draft...', { type: 'info' });

    setRecalling(true);
    try {
      const currentUserId = localStorage.getItem('userId') || undefined;
      await recallDocumentToDraft(documentId, currentUserId);

      const [doc, approvalRecords] = await Promise.all([
        getDocumentById(documentId),
        getDocumentApprovalInfo(documentId),
      ]);

      setExistingDocData({
        ...doc,
        approvalHistory: approvalRecords.length > 0 ? approvalRecords : (doc.approvalHistory || []),
      });
      setStatus('Draft');

      notify('Document recalled successfully. Approval flow has been reinitialized to Draft.', { type: 'success' });
      setTimeout(() => {
        redirect('list', 'documentsList');
      }, 600);
    } catch (error: any) {
      notify(error?.message || 'Failed to recall document', { type: 'error' });
    } finally {
      setRecalling(false);
    }
  };

  // const formatFileSize = (bytes: number) => {
  //   const mb = bytes / (1024 * 1024);
  //   return mb < 1 ? `${(bytes / 1024).toFixed(2)} KB` : `${mb.toFixed(2)} MB`;
  // };

  const getUserDisplayName = (rawValue?: string) => {
    if (!rawValue) return '-';
    const normalized = String(rawValue);
    return userNameMap[normalized] || normalized;
  };

  const renderApprovalComments = (comments?: string) => {
    const raw = String(comments || '').trim();
    if (!raw) return '';

    const tokenRegex = /(username|userId)\s*[=:]\s*([^\s,;，；]+)/gi;
    const nodes: Array<string | any> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRegex.exec(raw)) !== null) {
      const [fullMatch, key, value] = match;
      const start = match.index;

      if (start > lastIndex) {
        nodes.push(raw.slice(lastIndex, start));
      }

      const normalizedKey = String(key || '').toLowerCase();
      const rawValue = String(value || '').trim();
      const displayName = normalizedKey === 'userid' ? getUserDisplayName(rawValue) : rawValue;

      nodes.push(
        <span
          key={`delegated-user-${start}`}
          className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"
        >
          {displayName}
        </span>
      );

      lastIndex = start + fullMatch.length;
    }

    if (lastIndex < raw.length) {
      nodes.push(raw.slice(lastIndex));
    }

    return nodes.length > 0 ? nodes : raw;
  };

  const openDeputyModal = (stepInstanceId: string, fromUserId: string) => {
    setSelectedStepInstanceId(String(stepInstanceId));
    setSelectedFromUserId(String(fromUserId));
    setSelectedDeputyUserId('');
    setDeputyComment('');
    setShowDeputyModal(true);
  };

  const handleAssignDeputy = async () => {
    if (!documentId) return;
    if (!selectedStepInstanceId) {
      notify('Step instance not found', { type: 'warning' });
      return;
    }
    if (!selectedFromUserId) {
      notify('Original approver not found', { type: 'warning' });
      return;
    }
    if (!selectedDeputyUserId) {
      notify('Please select deputy approver', { type: 'warning' });
      return;
    }

    setSavingDeputy(true);
    try {
      await replaceApproverWithDeputy(
        selectedStepInstanceId,
        documentId,
        selectedFromUserId,
        selectedDeputyUserId,
        deputyComment
      );
      notify('Deputy approver assigned successfully', { type: 'success' });

      const refreshedApproval = await getDocumentApprovalInfo(documentId);
      setExistingDocData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          approvalHistory: refreshedApproval.length > 0 ? refreshedApproval : prev.approvalHistory,
        };
      });

      setShowDeputyModal(false);
      setSelectedStepInstanceId('');
      setSelectedFromUserId('');
      setSelectedDeputyUserId('');
      setDeputyComment('');
    } catch (error: any) {
      notify(error?.message || 'Failed to assign deputy approver', { type: 'error' });
    } finally {
      setSavingDeputy(false);
    }
  };

  if (loadingDoc) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading document...</div>
        </div>
      </div>
    );
  }

  if (documentId && !existingDoc) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="mb-4 text-red-600 font-semibold">Document not found</div>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-10 pt-5 pb-6 full-width">
     

      {/* 合并只读信息到表单顶部 */}
      <form onSubmit={handleSubmit}>
         {/* Header */}
      
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">
          {isRevision ? 'Document Revision' : documentId ? 'Edit Document' : 'Create New Document'}
        </h2>
        {!documentId && (
          <p className="text-sm text-slate-500 mt-1">
            Create a new document and configure the approval workflow
          </p>
        )}
        {isRevision && (
          <p className="text-sm text-slate-500 mt-1">
            Create a new revision of the document
          </p>
        )}
      

        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {documentId && existingDoc && (
              <>
                {/* <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Document Number</label>
                  <input type="text" value={existingDoc.documentNumber} disabled className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-900" />
                </div> */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Current Version</label>
                  <input
                    type="text"
                    value={formData.currentVersion}
                    onChange={(e) => setFormData({ ...formData, currentVersion: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded bg-white text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Status</label>
                  <input type="text" value={existingDoc.status} disabled className={`w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-900 ${
                    existingDoc.status === 'Approved' ? 'bg-green-50' :
                    existingDoc.status === 'Review' ? 'bg-yellow-50' :
                    existingDoc.status === 'Draft' ? 'bg-gray-50' :
                    'bg-red-50'
                  }`} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Created Date</label>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      format="dd/MM/yyyy"
                      value={toDatePickerValue(formData.createdAt || existingDoc.createdAt)}
                      onChange={(newValue) => setFormData({ ...formData, createdAt: toDateOnlyString(newValue) })}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: { '& .MuiInputBase-root': { height: 40 } },
                          inputProps: { placeholder: 'DD/MM/YYYY' },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <p className="text-[11px] text-gray-500 mt-1">Date only, no time.</p>
                </div>
              </>
            )}
          </div>

        {/* Basic Information */}
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                disabled={isRevision}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isRevision ? 'bg-gray-50 text-gray-500' : ''}`}
                placeholder="Enter document title"
              />
              {isRevision && <p className="text-xs text-gray-500 mt-1">Title cannot be changed in revision mode</p>}
              {!isRevision && titleValidation.status === 'checking' && (
                <p className="text-xs text-gray-500 mt-1">Checking title...</p>
              )}
              {!isRevision && titleValidation.status === 'invalid' && (
                <p className="text-xs text-red-600 mt-1">{titleValidation.message}</p>
              )}
              {!isRevision && titleValidation.status === 'valid' && (
                <p className="text-xs text-green-600 mt-1">{titleValidation.message || 'Title is available'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Document Number uses DocNum value below</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                DocNum *
              </label>
              <input
                type="text"
                value={docNum}
                onChange={(e) => setDocNum(e.target.value)}
                disabled={isRevision}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isRevision ? 'bg-gray-50 text-gray-500' : ''}`}
                placeholder="Please enter DocNum"
              />
              <p className="text-xs text-gray-500 mt-1">{isRevision ? `Document number cannot be changed in revision mode. Revision: ${(existingDoc?.revision || 0) + 1}` : 'Editable in edit mode (format: major.minor)'}</p>
              {!isRevision && docNumValidation.status === 'checking' && (
                <p className="text-xs text-gray-500 mt-1">Checking DocNum...</p>
              )}
              {!isRevision && docNumValidation.status === 'invalid' && (
                <p className="text-xs text-red-600 mt-1">{docNumValidation.message}</p>
              )}
              {!isRevision && docNumValidation.status === 'valid' && (
                <p className="text-xs text-green-600 mt-1">{docNumValidation.message || 'DocNum is valid'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Owner *
              </label>
              <Autocomplete
                options={approversPool}
                value={selectedOwner}
                onChange={(_, value) => {
                  setFormData({ ...formData, ownerId: value?.id || '' });
                }}
                getOptionLabel={(option) => option.name || option.email || option.id}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No users found"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select owner"
                    size="small"
                  />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: 42,
                    borderRadius: '0.5rem',
                  },
                }}
              />
              {selectedOwner && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedOwner.role}{selectedOwner.email ? ` • ${selectedOwner.email}` : ''}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter document description"
              />
            </div>

            {documentId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Version Change Notes
                </label>
                <textarea
                  value={formData.versionChanges}
                  onChange={(e) => setFormData({ ...formData, versionChanges: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the main content of this revision"
                />
              </div>
            )}
          </div>
        </div>

        {/* Document File */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Document File</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SharePoint URL</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={sharepointUrl}
                onChange={(e) => setSharepointUrl(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Paste URL, or click Browse to pick from SharePoint"
              />
              <button
                type="button"
                onClick={() => setShowSpBrowser(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <FolderOpen className="w-4 h-4" />
                Browse
              </button>
              {sharepointUrl && (
                <>
                  <a
                    href={getOfficeHref(normalizeSharepointUrl(sharepointUrl))}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
                  >
                    Preview
                  </a>
                  <button
                    type="button"
                    onClick={() => setSharepointUrl('')}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-2">Paste a SharePoint URL directly, or click Browse to select a file from SharePoint.</p>
          </div>
        </div>

        {/**
          {uploadedFile ? (
            // New file uploaded
            <div>
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
                    <Upload className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">New File Ready to Upload</p>
                    <p className="text-sm text-gray-600">{uploadedFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(uploadedFile.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {documentId && existingFileUrl && (
                <p className="text-xs text-gray-500">This will replace the current document file.</p>
              )}
            </div>
          ) : documentId && existingFileUrl ? (
            // Existing file in edit mode
            <div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                      <Upload className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Current Document File</p>
                      <p className="text-sm text-gray-600">{existingFileUrl.split('/').pop() || 'Document'}</p>
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-100 rounded cursor-pointer transition-colors">
                    Replace File
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                    />
                  </label>
                </div>
              </div>
              <p className="text-xs text-gray-500">Leave empty to keep using the current file, or upload a new file to replace it.</p>
            </div>
          ) : (
            // New document or no existing file
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">Click to upload document</p>
              <p className="text-sm text-gray-500 mb-4">Supported formats: PDF, Word, Excel, etc. (Max 50MB)</p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                <Upload className="w-4 h-4" />
                Choose File
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                />
              </label>
            </div>
          )}
          */}

        {/* Approval Workflow */}
        {!isOverwrite && <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Approval Workflow Configuration</h3>
              <p className="text-sm text-gray-600 mt-1">Configure the approval workflow for this document</p>
            </div>
            <button
              type="button"
              onClick={() => setShowApprovalSection(!showApprovalSection)}
              className="text-gray-500 hover:text-gray-700"
            >
              {showApprovalSection ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
            </button>
          </div>

          {showApprovalSection && (
            <div className="space-y-4">
              {/* Approval Steps */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Approval Steps</h3>
                    <p className="text-sm text-slate-500 mt-0.5">{template.steps.length} step{template.steps.length !== 1 ? 's' : ''} configured</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddStep}
                    className="inline-flex items-center gap-2 px-4 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
                  >
                    <Plus className="size-4" />
                    Add Step
                  </button>
                </div>

                {template.steps.length === 0 ? (
                  <div className="text-center py-14 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl">
                    <WorkflowIcon className="size-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium mb-1">No approval steps yet</p>
                    <p className="text-sm text-gray-400 mb-4">Add steps to define the approval workflow</p>
                    <button
                      type="button"
                      onClick={handleAddStep}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
                    >
                      <Plus className="size-4" />
                      Add First Step
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {template.steps.map((step: ApprovalStep, index: number) => (
                      <div key={step.id}>
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          {/* Step Header */}
                          <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                                {step.stepNo}
                              </div>
                              <span className="font-semibold text-slate-800 text-sm">{step.Name || `Step ${step.stepNo}`}</span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${step.stepType === 'parallel' ? 'bg-purple-100 text-purple-800' :
                                  step.stepType === 'conditional' ? 'bg-orange-100 text-orange-800' :
                                    'bg-blue-100 text-blue-800'
                                }`}>
                                {step.stepType === 'parallel' ? 'Parallel' : step.stepType === 'conditional' ? 'Conditional' : 'Single'}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveStep(step.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="size-3.5" />
                              Delete
                            </button>
                          </div>

                          {/* Step Body */}
                          <div className="p-5">
                            <div className="grid grid-cols-2 gap-4 mb-5">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Step Name</label>
                                <input
                                  type="text"
                                  value={step.Name}
                                  onChange={(e) => updateStepField(step.id, 'Name', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Step Type</label>
                                <select
                                  value={step.stepType}
                                  disabled
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm bg-gray-100 cursor-not-allowed"
                                >
                                  <option value="parallel">Parallel Approval</option>
                                </select>
                              </div>
                              <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                                <textarea
                                  value={step.description}
                                  onChange={(e) => updateStepField(step.id, 'description', e.target.value)}
                                  rows={2}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                />
                              </div>
                            </div>

                            {/* Approvers */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">Approvers</label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCurrentStepId(step.id);
                                    setSelectedDepartmentTitle('all');
                                    setApproverKeyword('');
                                    setShowApproverModal(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
                                >
                                  <Plus className="size-3.5" />
                                  Add Approver
                                </button>
                              </div>
                              {step.approvers.length === 0 ? (
                                <div className="text-center py-5 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-400">
                                  No approvers assigned yet
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {/* Department Titles */}
                                  {step.approvers.some(a => a.selectorType === 'departmentTitle') && (
                                    <div>
                                      <div className="text-xs font-semibold text-purple-600 mb-1.5 uppercase tracking-wide">Department Titles</div>
                                      <div className="flex flex-wrap gap-2">
                                        {step.approvers.filter(a => a.selectorType === 'departmentTitle').map(approver => (
                                          <div
                                            key={approver.id}
                                            className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm"
                                          >
                                            <UserCircle className="size-4 text-purple-500 flex-shrink-0" />
                                            <div>
                                              <div className="font-medium text-gray-900 text-sm">{approver.departmentTitle || approver.name}</div>
                                              <div className="text-xs text-purple-600">Dynamic by Department Title</div>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveApprover(step.id, approver.id)}
                                              className="ml-1 text-gray-400 hover:text-red-600"
                                            >
                                              <X className="size-3.5" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {/* Persons */}
                                  {step.approvers.some(a => a.selectorType !== 'departmentTitle') && (
                                    <div>
                                      <div className="text-xs font-semibold text-blue-600 mb-1.5 uppercase tracking-wide">Persons</div>
                                      <div className="flex flex-wrap gap-2">
                                        {step.approvers.filter(a => a.selectorType !== 'departmentTitle').map(approver => (
                                          <div
                                            key={approver.id}
                                            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm"
                                          >
                                            <UserCircle className="size-4 text-blue-500 flex-shrink-0" />
                                            <div>
                                              <div className="font-medium text-gray-900 text-sm">{approver.name}</div>
                                              <div className="text-xs text-gray-500">{approver.departmentTitle || approver.role}</div>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveApprover(step.id, approver.id)}
                                              className="ml-1 text-gray-400 hover:text-red-600"
                                            >
                                              <X className="size-3.5" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {index < template.steps.length - 1 && (
                          <div className="flex justify-center py-2">
                            <div className="w-0.5 h-5 bg-gray-300 rounded"></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>


            </div>
          )}
        </div>}

        {/* Approval History */}
        {!isOverwrite && <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-5 shadow-sm">
          <h3 className="text-gray-900 text-lg font-semibold mb-4">Approval History</h3>
          {(existingDoc?.approvalHistory || []).length > 0 ? (
            <div className="space-y-3">
              {(existingDoc?.approvalHistory || []).map((record) => (
                <div key={record.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{getUserDisplayName(record.approver)}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-600 text-sm">Level {record.level}</span>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          record.action === 'Approved'
                            ? 'bg-green-100 text-green-700'
                            : record.action === 'Rejected'
                            ? 'bg-red-100 text-red-700'
                            : record.action === 'Deputy'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {record.action === 'Approved' && 'Approved'}
                        {record.action === 'Rejected' && 'Rejected'}
                        {record.action === 'Deputy' && 'DEPUTY'}
                        {record.action === 'Pending' && 'Pending'}
                      </span>
                    </div>
                    <span className="text-gray-500 text-sm flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(record.timestamp).toLocaleString('en-GB')}
                    </span>
                  </div>
                  {record.action === 'Pending' && String(existingDoc?.status ?? status).trim().toLowerCase() !== 'draft' && (
                    <div className="mb-2">
                      <button
                        type="button"
                        onClick={() => openDeputyModal(record.stepInstanceId || '', record.approverId || record.approver)}
                        disabled={!record.stepInstanceId}
                        className="text-xs px-2.5 py-1 border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50 transition-colors"
                      >
                        Assign Deputy
                      </button>
                    </div>
                  )}
                  {record.comments && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-gray-700 text-sm"><span className="font-medium">Comments:</span> {renderApprovalComments(record.comments)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-600">No approval history available.</div>
          )}
        </div>}

        {/* Actions */}
        <div className="flex gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          {/* Delete Button - Only show for Draft status and existing documents */}
          {documentId && status === 'Draft' && (
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              disabled={saving || deleting}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 border border-red-300 text-red-600 rounded-[10px] hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              title="Delete Draft Document"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
          {documentId && isInReviewStatus && (
            <button
              type="button"
              onClick={handleRecallToDraft}
              disabled={saving || recalling || deleting}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 border border-amber-300 text-amber-700 rounded-[10px] hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              title="Recall Document to Draft"
            >
              <RotateCcw className="w-4 h-4" />
              {recalling ? 'Recalling...' : 'Recall'}
            </button>
          )}
          <button
            type="button"
            onClick={handleBack}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 border border-gray-300 text-slate-700 rounded-[10px] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveAsDraft}
            disabled={saving || validatingDocNum}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
          {/* <button
            type="button"
            onClick={handleSubmitForReview}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {saving ? 'Submitting...' : 'Submit for Review'}
          </button> */}
          <button
            id="submit-btn"
            type="submit"
            className="hidden"
          >
            Submit
          </button>
        </div>
      </form>

      {/* Approver Selection Modal */}
      {showApproverModal && currentStepId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Select Approver</h3>
                <button
                  type="button"
                  onClick={closeApproverModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Department Title
                      </label>
                      <select
                        value={selectedDepartmentTitle}
                        onChange={(e) => setSelectedDepartmentTitle(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Department Titles</option>
                        {departmentTitleOptions.map(option => (
                          <option key={`${option.id}-${option.title}`} value={option.title}>{option.title}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Person
                      </label>
                      <input
                        type="text"
                        value={approverKeyword}
                        onChange={(e) => setApproverKeyword(e.target.value)}
                        placeholder="Search by name or email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-500">
                      {filteredApprovers.length} people available
                    </p>
                    <button
                      type="button"
                      disabled={selectedDepartmentTitle === 'all' || !selectedDepartmentTitle || !!currentStep?.approvers.some((a: Approver) => a.id === buildDepartmentSelectorId(selectedDepartmentTitle, selectedDepartmentTitleId))}
                      onClick={() => {
                        if (selectedDepartmentTitle && selectedDepartmentTitle !== 'all' && currentStepId) {
                          handleAddApprover(currentStepId, createDepartmentTitleApprover(selectedDepartmentTitle, selectedDepartmentTitleId));
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-700 rounded-md text-xs font-semibold hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="size-3.5" />
                      Save Department Title
                    </button>
                  </div>
                </div>

                {filteredApprovers.length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-10 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                    No user matched the selected department/person condition.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                    {filteredApprovers.map(approver => {
                      const isAlreadyAdded = currentStep?.approvers.some((a: Approver) => a.id === approver.id || (a.userId && a.userId === approver.id));

                      return (
                        <button
                          key={approver.id}
                          type="button"
                          onClick={() => {
                            if (!isAlreadyAdded && currentStepId) {
                              handleAddApprover(currentStepId, approver);
                            }
                          }}
                          disabled={isAlreadyAdded}
                          className={`p-4 border rounded-lg text-left transition-colors ${isAlreadyAdded
                              ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                              : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <UserCircle className="size-10 text-gray-400" />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{approver.name}</div>
                              <div className="text-sm text-gray-600">{approver.departmentTitle || approver.role}</div>
                              <div className="text-xs text-gray-500">{approver.email}</div>
                              <div className="mt-1 text-[11px] font-semibold text-blue-600">Person</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeApproverModal}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Delete Document</h3>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium mb-1">Are you sure you want to delete this document?</p>
                    <p className="text-sm text-gray-600">This action cannot be undone. The document and all its associated data will be permanently removed.</p>
                  </div>
                </div>
                {existingDoc && (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                    <div className="text-sm"><span className="font-medium">Title:</span> {existingDoc.title}</div>
                    <div className="text-sm"><span className="font-medium">Number:</span> {existingDoc.documentNumber}</div>
                    <div className="text-sm"><span className="font-medium">Status:</span> <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">{existingDoc.status}</span></div>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeputyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Assign Deputy Approver</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deputy User *
                </label>
                <select
                  value={selectedDeputyUserId}
                  onChange={(e) => setSelectedDeputyUserId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={savingDeputy}
                >
                  <option value="">-- Select user --</option>
                  {allUsers.map((u: any) => {
                    const userId = u?.id ?? u?.Id ?? u?.userId;
                    const userName = u?.userName ?? u?.username ?? u?.name;
                    if (userId == null) return null;
                    return (
                      <option key={String(userId)} value={String(userId)}>
                        {String(userName || userId)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment (Optional)
                </label>
                <textarea
                  value={deputyComment}
                  onChange={(e) => setDeputyComment(e.target.value)}
                  placeholder="Reason for deputy assignment"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  disabled={savingDeputy}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeputyModal(false);
                  setSelectedStepInstanceId('');
                  setSelectedFromUserId('');
                  setSelectedDeputyUserId('');
                  setDeputyComment('');
                }}
                disabled={savingDeputy}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssignDeputy}
                disabled={savingDeputy || !selectedDeputyUserId}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingDeputy ? 'Saving...' : 'Confirm Deputy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSpBrowser && (
        <SharePointBrowser
          onPick={(url) => {
            setSharepointUrl(url);
            setShowSpBrowser(false);
          }}
          onClose={() => setShowSpBrowser(false)}
        />
      )}
    </div>
  );
}

export default DocumentManageEdit;